import { ChangeEvent, useState } from 'react';

import { getMyBizCounts } from '@/api/pc-api/v1/mybiz/counts';
import { getMyBizListLNB, LNBMyBizListItem } from '@/api/pc-api/v2/mybiz/list/lnb';
import LNB from '@/components/common/LNB';
import { BIZ_TYPE } from '@/constants/bizType';
import { ROUTES } from '@/constants/routes';
import { useInitialPageFetch } from '@/hooks/useInitialPageFetch';
import usePrefixNavigate from '@/hooks/usePrefixNavigate';
import LNBAccountSearchItem from '@/outlets/ContainerOutlet/LNBLayout/LNBAccountSearchItem';
import { useAuthStore } from '@/store/auth';
import { getCompanyName, getSubCompanyName } from '@/utils/myBiz';

const LNBAccountSearch = () => {
  const { navigate } = usePrefixNavigate();
  const { authInfoId, partnerName, dealerName, bizTargetType, bizType, role } = useAuthStore();
  const myBizCounts = useInitialPageFetch(() => getMyBizCounts());
  const [query, setQuery] = useState(''); // input에 입력되는 상태
  const [searchedQuery, setSearchedQuery] = useState(''); // 검색된 쿼리를 기준으로 검색결과 하이라이팅과 선택된 myBiz를 최상단으로 올리는 작업을 처리하기 위한 상태
  const [myBizList, setMyBizList] = useState<LNBMyBizListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const search = async (query: string) => {
    setIsError(false);
    setIsLoading(true);

    const trimmedQuery = query.trim();

    try {
      const {
        data: { data }
      } = await getMyBizListLNB(trimmedQuery);
      setSearchedQuery(trimmedQuery);

      setMyBizList(data);
    } catch {
      setIsError(true);
    }

    setIsLoading(false);
  };

  const select = (myBiz: LNBMyBizListItem) => {
    navigate(ROUTES.DASHBOARD, undefined, myBiz.authInfoId);
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const onClear = () => {
    setQuery('');
    search('');
  };

  if (!bizType || !role || !bizTargetType) return null;
  const isSelectedMyBizExist = !searchedQuery || myBizList.some((myBiz) => myBiz.authInfoId === authInfoId);

  return (
    /* TODO: 1차 오픈 후 레이어로 분리 필요 */
    /* ref: https://github.daumkakao.com/zzng/partner-center-front/pull/375#discussion_r852571 */
    <LNB.AccountSearch
      selectedAccountName={getCompanyName(partnerName, dealerName)}
      query={query}
      onClear={onClear}
      onChange={onChange}
      onSearch={() => search(query)}
      viewAllHref={ROUTES.ROOT}
      totalCount={myBizCounts?.data || 0}
      isLoading={isLoading}
      isError={isError}
    >
      {isSelectedMyBizExist && (
        <LNBAccountSearchItem
          bizType={BIZ_TYPE[bizType]}
          companyName={getCompanyName(partnerName, dealerName)}
          subCompanyName={getSubCompanyName(bizTargetType, dealerName)}
          role={role}
          highlightedQuery={searchedQuery}
          selected
        />
      )}
      {myBizList
        .filter((mybiz) => mybiz.authInfoId !== authInfoId)
        .map((myBiz) => (
          <LNBAccountSearchItem
            key={myBiz.authInfoId}
            bizType={BIZ_TYPE[myBiz.bizType]}
            companyName={getCompanyName(myBiz.partnerCompanyName, myBiz.dealerCompanyName)}
            subCompanyName={getSubCompanyName(myBiz.bizTargetType, myBiz.dealerCompanyName)}
            role={myBiz.role}
            highlightedQuery={searchedQuery}
            onClick={() => select(myBiz)}
            selected={myBiz.authInfoId === authInfoId}
          />
        ))}
    </LNB.AccountSearch>
  );
};

export default LNBAccountSearch;
